import React, { useEffect, useMemo, useState, useRef } from "react";
import DocumentToolbar from "../common/DocumentToolbar";
import useDocumentDraft from "../../hooks/useDocumentDraft";
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Table,
  Modal,
  Spinner,
} from "react-bootstrap";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch"; // ajustează dacă ai alt path

function makeUid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function emptyLine() {
  return {
    uid: makeUid(),
    id: null,
    amount: 0,
    bxexplic: "",
    ref_rec_type: null,
    ref_doc_id: null,
    // doar pt UI dropdown factură
    invoice_opt: null,
  };
}

function toISODate(d) {
  if (!d) return "";
  if (typeof d === "string" && d.match(/^\d{4}-\d{2}-\d{2}/)) {
    return d.substring(0, 10);
  }
  const dateObj = d instanceof Date ? d : new Date(d);
  if (isNaN(dateObj.getTime())) return "";
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

async function readJsonSmart(r) {
  if (!r) return null;
  if (r.__json) return r.__json;
  if (typeof r.json === "function") return await r.json();
  return r;
}

async function fetchFirstOk(urls) {
  let lastErr = null;
  for (const u of urls) {
    try {
      const r = await authFetch(u);
      // authFetch poate întoarce Response sau direct JSON
      if (r && typeof r === "object" && "ok" in r) {
        if (r.ok) return r;
      } else {
        return r;
      }
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) console.warn("fetchFirstOk: all failed", lastErr);
  return null;
}

export function CashDocForm({ initialDoc, onSubmit, busy, isClosed, documentActions }) {
  const formElement = useRef(null);
  const [err, setErr] = useState("");
  const [searchParams] = useSearchParams();
  const queryRecType = searchParams.get("rec_type");
  const queryAccountId = searchParams.get("account_id");

  // header
  const [recType, setRecType] = useState(() => {
    if (initialDoc?.rec_type) return initialDoc.rec_type;
    if (queryRecType === "20") return 20;
    if (queryRecType === "21") return 21;
    return 21;
  }); // 20 incasare, 21 plata

  const [paymentMode, setPaymentMode] = useState(() => {
    if (initialDoc) {
      if (initialDoc.contact_id) {
        const lns = initialDoc.lines || initialDoc.linii || [];
        const hasRef = lns.some((l) => l.ref_doc_id);
        return hasRef ? "furnizori" : "contacte";
      }
      return "none";
    }
    return "furnizori";
  });

  const [docNo, setDocNo] = useState(initialDoc?.doc_no ?? "");
  const [docDate, setDocDate] = useState(() => {
    const v = initialDoc?.doc_date;
    return v ? new Date(v) : new Date();
  });
  // lock editing when document is finalized
  const locked = !!isClosed || !!initialDoc?.bxfinal_bool;

  const [explic, setExplic] = useState(initialDoc?.explic ?? "");

  // dropdowns
  const [selContact, setSelContact] = useState(null);
  const [selAccount, setSelAccount] = useState(null);
  const [selContraAccount, setSelContraAccount] = useState(null);

  // lines
  const [lines, setLines] = useState(() => {
    const lns = initialDoc?.lines || initialDoc?.linii || [];
    if (!lns.length) return [emptyLine()];

    return lns.map((x) => ({
      uid: makeUid(),
      id: x.id ?? null,
      amount: Number(x.amount ?? 0),
      bxexplic: x.bxexplic ?? "",
      ref_rec_type: x.ref_rec_type ?? null,
      ref_doc_id: x.ref_doc_id ?? null,
      invoice_opt: x.ref_doc_id
        ? {
            value: x.ref_doc_id,
            label: x.ref_label || x.ref_doc_no || `Factura #${x.ref_doc_id}`,
          }
        : null,
    }));
  });

  const disabledAll = !!busy || locked;
  const isPlata = Number(recType) === 21;

  const [curs, setCurs] = useState(initialDoc?.curs ?? 1.0);
  const isForeignCurrency = selAccount ? (selAccount.bxvaluta_id !== 1) : false;

  useEffect(() => {
    if (initialDoc) {
      setCurs(initialDoc.curs ?? 1.0);
    }
  }, [initialDoc]);

  useEffect(() => {
    const fetchCurs = async () => {
      const valutaId = selAccount?.bxvaluta_id;
      if (!valutaId || valutaId === 1) {
        setCurs(1.0);
        return;
      }
      if (initialDoc && initialDoc.curs) {
        return;
      }
      if (docDate) {
        try {
          const dateObj = new Date(docDate);
          if (!isNaN(dateObj.getTime())) {
            const dateStr = dateObj.toISOString().substring(0, 10);
            const r = await authFetch(`/bxvaluta/curs/fetch?valuta_id=${valutaId}&date=${dateStr}`);
            if (r && r.rate !== undefined) {
              setCurs(Number(r.rate) || 1.0);
            }
          }
        } catch (e) {
          console.error("Failed to fetch BNR exchange rate", e);
        }
      }
    };
    fetchCurs();
  }, [selAccount?.bxvaluta_id, docDate, initialDoc]);

  // init dropdown selections from initialDoc or searchParams
  useEffect(() => {
    if (initialDoc?.contact_id) {
      setSelContact({
        value: initialDoc.contact_id,
        label:
          initialDoc.contact_label ||
          initialDoc.contact_name ||
          initialDoc.bxdenumire ||
          `Contact #${initialDoc.contact_id}`,
      });
    } else {
      setSelContact(null);
    }

    if (initialDoc?.contra_account_id) {
      setSelContraAccount({
        value: initialDoc.contra_account_id,
        label:
          initialDoc.contra_account_label ||
          initialDoc.contra_account_name ||
          `Cont #${initialDoc.contra_account_id}`,
      });
    } else {
      setSelContraAccount(null);
    }

    if (initialDoc?.cash_account_id) {
      setSelAccount({
        value: initialDoc.cash_account_id,
        label:
          initialDoc.cash_account_label ||
          initialDoc.cash_account_name ||
          initialDoc.account_label ||
          `Cont #${initialDoc.cash_account_id}`,
        bxvaluta_id: initialDoc.bxvaluta_id || 1,
      });
    } else if (!initialDoc && queryAccountId) {
      (async () => {
        try {
          const acc = await authFetch(`/accounts/${queryAccountId}`);
          if (acc) {
            setSelAccount({
              value: acc.id,
              label: `${acc.code} - ${acc.name}`,
              bxvaluta_id: acc.bxvaluta_id || acc.valuta_id || 1,
            });
          }
        } catch (e) {
          console.error("Nu s-a putut încărca contul din query params:", e);
        }
      })();
    } else {
      setSelAccount(null);
    }
  }, [initialDoc, queryAccountId]);

  const total = useMemo(() => {
    const s = lines.reduce((acc, ln) => acc + (Number(ln.amount) || 0), 0);
    return Math.round(s * 100) / 100;
  }, [lines]);

  function setLine(uid, patch) {
    setLines((prev) =>
      prev.map((l) => (l.uid === uid ? { ...l, ...patch } : l)),
    );
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine()]);
  }

  function removeLine(uid) {
    setLines((prev) =>
      prev.length <= 1 ? prev : prev.filter((l) => l.uid !== uid),
    );
  }

  // ---------------- loaders --------------  // Contacte
  const contactBases = ["/contacte/"];
  const loadContacts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const url = (base) =>
        `${base}?skip=0&limit=20&search=${encodeURIComponent(q)}`;

      const r = await fetchFirstOk(contactBases.map(url));
      if (!r) return [];

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      return items.map((c) => ({
        value: c.id,
        label: c.bxdenumire || c.denumire || c.name || `Contact #${c.id}`,
      }));
    } catch (e) {
      console.error("loadContacts failed", e);
      return [];
    }
  };

  // Furnizori cu sold (plata furnizori)
  const loadSuppliersWithRest = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const cashAccQuery = selAccount?.value ? `&cash_account_id=${encodeURIComponent(selAccount.value)}` : "";
      const url = `/intrare/rest/sumar?eps=0.01&limit=50&switch_one=1&q=${encodeURIComponent(q)}${cashAccQuery}`;

      const r = await authFetch(url);
      if (!r) return [];

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      return items.map((c) => ({
        value: c.contact_id,
        label: `${c.contact_name} (Sold: ${Number(c.total_sold).toFixed(2)})`,
      }));
    } catch (e) {
      console.error("loadSuppliersWithRest failed", e);
      return [];
    }
  };

  // Clienți cu sold (încasare clienți)
  const loadClientsWithRest = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const cashAccQuery = selAccount?.value ? `&cash_account_id=${encodeURIComponent(selAccount.value)}` : "";
      const url = `/iesire/rest/sumar?eps=0.01&limit=50&switch_one=11&q=${encodeURIComponent(q)}${cashAccQuery}`;

      const r = await authFetch(url);
      if (!r) return [];

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      return items.map((c) => ({
        value: c.contact_id,
        label: `${c.contact_name} (Sold: ${Number(c.total_sold).toFixed(2)})`,
      }));
    } catch (e) {
      console.error("loadClientsWithRest failed", e);
      return [];
    }
  };

  // Conturi - ai /accounts
  const accountBases = ["/accounts"];
  const loadAccounts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const url = (base) =>
        `${base}?skip=0&limit=20&search=${encodeURIComponent(q)}`;

      const r = await fetchFirstOk(accountBases.map(url));
      if (!r) return [];

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      return items.map((a) => ({
        value: a.id,
        label:
          `${a.code || ""}${a.code ? " - " : ""}${a.name || ""}`.trim() ||
          `Cont #${a.id}`,
        bxvaluta_id: a.bxvaluta_id || a.valuta_id || 1,
      }));
    } catch (e) {
      console.error("loadAccounts failed", e);
      return [];
    }
  };

  const loadContraAccounts = async (inputValue) => {
    try {
      const q = (inputValue || "").trim();
      const switchParam =
        paymentMode === "contacte"
          ? "&switch=2"
          : paymentMode === "none"
          ? "&switch=0"
          : "";
      const url = `/accounts?skip=0&limit=50&search=${encodeURIComponent(q)}${switchParam}`;

      const r = await authFetch(url);
      if (!r) return [];

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      return items.map((a) => ({
        value: a.id,
        label:
          `${a.code || ""}${a.code ? " - " : ""}${a.name || ""}`.trim() ||
          `Cont #${a.id}`,
      }));
    } catch (e) {
      console.error("loadContraAccounts failed", e);
      return [];
    }
  };

  // Facturi neplătite/neîncasate (sold <> 0): GET /intrare/rest sau GET /iesire/rest
  const loadInvoices = async (inputValue) => {
    try {
      if (!selContact?.value) return [];
      const q = (inputValue || "").trim();
      const cashAccQuery = selAccount?.value ? `&cash_account_id=${encodeURIComponent(selAccount.value)}` : "";

      const url = isPlata
        ? `/intrare/rest?skip=0&limit=50&q=${encodeURIComponent(q)}&contact_id=${encodeURIComponent(selContact.value)}&switch_one=1${cashAccQuery}`
        : `/iesire/rest?skip=0&limit=50&q=${encodeURIComponent(q)}&contact_id=${encodeURIComponent(selContact.value)}&switch_one=11${cashAccQuery}`;

      const r = await authFetch(url);
      if (!r) return [];

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      return items.map((inv) => {
        const id = inv.id;
        const no = inv.bxdoc_no || inv.doc_no || `#${id}`;
        const dtRaw = inv.bxdoc_date || inv.doc_date || "";
        const dt = dtRaw ? String(dtRaw).slice(0, 10) : "";
        const sold = inv.bxsold ?? 0;
        const valuta = inv.valuta_cod || "";

        const labelParts = [no];
        if (dt) labelParts.push(formatDateRo(dt));
        labelParts.push(`sold ${Number(sold).toFixed(2)}${valuta ? ` ${valuta}` : ""}`);

        return {
          value: id,
          label: labelParts.join(" | "),
          _raw: inv,
        };
      });
    } catch (e) {
      console.error("loadInvoices failed", e);
      return [];
    }
  };

  // ---------------- Modal distribuire ----------------
  const [showAlloc, setShowAlloc] = useState(false);
  const [allocAmount, setAllocAmount] = useState(0);

  const [invLoading, setInvLoading] = useState(false);
  const [invErr, setInvErr] = useState("");
  const [invoices, setInvoices] = useState([]); // [{id, doc_no, doc_date, remaining}]
  const [allocRows, setAllocRows] = useState({}); // { [id]: {use, alloc} }

  const allocSum = useMemo(() => {
    return Object.values(allocRows).reduce(
      (acc, r) => acc + (Number(r?.alloc) || 0),
      0,
    );
  }, [allocRows]);

  function setAlloc(invId, patch) {
    setAllocRows((prev) => ({
      ...prev,
      [invId]: { ...(prev[invId] || { use: true, alloc: 0 }), ...patch },
    }));
  }

  function openAllocModal() {
    // prefill: dacă nu ai introdus nimic, poți porni de la total curent (doar dacă e > 0)
    if (!allocAmount && total) setAllocAmount(total);
    setShowAlloc(true);
    loadInvoicesForSupplier();
  }

  function closeAllocModal() {
    setShowAlloc(false);
    setInvErr("");
  }

  async function loadInvoicesForSupplier() {
    setInvErr("");
    setInvLoading(true);
    try {
      if (!selContact?.value) {
        setInvoices([]);
        setAllocRows({});
        return;
      }
      const cashAccQuery = selAccount?.value ? `&cash_account_id=${encodeURIComponent(selAccount.value)}` : "";

      const url = isPlata
        ? `/intrare/rest?skip=0&limit=100&contact_id=${encodeURIComponent(selContact.value)}&switch_one=1${cashAccQuery}&q=`
        : `/iesire/rest?skip=0&limit=100&contact_id=${encodeURIComponent(selContact.value)}&switch_one=11${cashAccQuery}&q=`;

      const r = await authFetch(url);
      if (!r) {
        setInvoices([]);
        setAllocRows({});
        return;
      }

      const data = await readJsonSmart(r);
      const items = data.items || data.results || data || [];

      const mapped = items.map((inv) => {
        const id = inv.id;
        const doc_no = inv.bxdoc_no || inv.doc_no || `#${id}`;
        const dtRaw = inv.bxdoc_date || inv.doc_date || "";
        const doc_date = dtRaw ? String(dtRaw).slice(0, 10) : "";
        const remaining = inv.bxsold ?? 0;
        const valuta = inv.valuta_cod || "";

        return {
          id,
          doc_no,
          doc_date,
          remaining: Number(remaining) || 0,
          valuta,
        };
      });

      // FIFO
      mapped.sort((a, b) =>
        String(a.doc_date).localeCompare(String(b.doc_date)),
      );

      setInvoices(mapped);

      const init = {};
      for (const inv of mapped) init[inv.id] = { use: true, alloc: 0 };
      setAllocRows(init);

      // auto FIFO la deschidere
      const amt = Number(allocAmount) || 0;
      if (amt !== 0) autoDistribute(amt, mapped, init);
    } catch (e) {
      console.error("loadInvoicesForSupplier failed", e);
      setInvErr(String(e?.message || e));
      setInvoices([]);
      setAllocRows({});
    } finally {
      setInvLoading(false);
    }
  }

  function autoDistribute(amount, invList = invoices, currentRows = allocRows) {
    let left = Number(amount) || 0;
    const next = { ...currentRows };

    // reset alloc
    for (const inv of invList) {
      const row = next[inv.id] || { use: true, alloc: 0 };
      next[inv.id] = { ...row, alloc: 0 };
    }

    for (const inv of invList) {
      if (left === 0) break;
      const row = next[inv.id] || { use: true, alloc: 0 };
      if (!row.use) continue;

      const cap = Math.max(0, Number(inv.remaining) || 0);
      if (cap <= 0) continue;

      // plăți normale: left pozitiv
      const pay = left > 0 ? Math.min(left, cap) : Math.max(left, -cap);
      next[inv.id] = { ...row, alloc: pay };
      left -= pay;
    }

    setAllocRows(next);
  }

  function applyAllocationToLines() {
    const newLines = [];
    for (const inv of invoices) {
      const r = allocRows[inv.id];
      if (!r?.use) continue;
      const a = Number(r.alloc) || 0;
      if (a === 0) continue;

      newLines.push({
        uid: makeUid(),
        id: null,
        amount: a,
        bxexplic: isPlata ? `Plată ${inv.doc_no}` : `Încasare ${inv.doc_no}`,
        ref_rec_type: isPlata ? 0 : 5,
        ref_doc_id: inv.id,
        invoice_opt: {
          value: inv.id,
          label: `${inv.doc_no} | ${formatDateRo(inv.doc_date)}${inv.valuta ? ` | ${inv.valuta}` : ""}`,
        },
      });
    }

    setLines(newLines.length ? newLines : [emptyLine()]);
    setShowAlloc(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");

    if (!docNo) {
      setErr("Introduceți numărul de document.");
      return;
    }
    if (!selAccount) {
      setErr("Alegeți contul de casă/bancă.");
      return;
    }

    if (paymentMode === "furnizori") {
      if (!selContact) {
        setErr(isPlata ? "Alegeți furnizorul." : "Alegeți clientul.");
        return;
      }
    } else if (paymentMode === "contacte") {
      if (!selContraAccount) {
        setErr("Alegeți un cont corespondent (contra cont).");
        return;
      }
      if (!selContact) {
        setErr("Alegeți contactul.");
        return;
      }
    } else if (paymentMode === "none") {
      if (!selContraAccount) {
        setErr("Alegeți un cont corespondent (contra cont).");
        return;
      }
    }

    const activeLines = lines.filter((l) => Number(l.amount) !== 0);
    if (activeLines.length === 0) {
      setErr("Documentul trebuie să aibă cel puțin o linie cu sumă diferită de zero.");
      return;
    }

    if (paymentMode === "furnizori") {
      for (let i = 0; i < activeLines.length; i++) {
        if (!activeLines[i].ref_doc_id) {
          const errMsg = isPlata
            ? `Linia ${i + 1} nu are factură asociată. Pentru plăți fără factură specifică, folosiți opțiunile "Listă contacte" sau "Fără contacte".`
            : `Linia ${i + 1} nu are factură asociată. Pentru încasări fără factură specifică, folosiți opțiunile "Listă contacte" sau "Fără contacte".`;
          setErr(errMsg);
          return;
        }
      }
    }

    const payload = {
      rec_type: Number(recType),
      doc_no: docNo,
      doc_date: toISODate(docDate),

      contact_id: selContact?.value ?? null,
      contra_account_id: selContraAccount?.value ?? null,
      cash_account_id: selAccount?.value ?? null,
      curs: isForeignCurrency ? (Number(curs) || 1.0) : 1.0,
      bxvaluta_id: selAccount?.bxvaluta_id ?? 1,

      explic: explic || "",
      lines: activeLines.map((l) => ({
        id: l.id ?? undefined,
        amount: Number(l.amount) || 0,
        bxexplic: l.bxexplic || "",
        ref_rec_type: (l.ref_rec_type !== null && l.ref_rec_type !== undefined) ? Number(l.ref_rec_type) : null,
        ref_doc_id: (l.ref_doc_id !== null && l.ref_doc_id !== undefined) ? Number(l.ref_doc_id) : null,
      })),
    };

    try {
      await onSubmit(payload);
    } catch (e2) {
      setErr(String(e2?.message || e2));
    }
  }

  const draft = useDocumentDraft({ recType, paymentMode, docNo, docDate, explic, curs,
    contact: selContact?.value, account: selAccount?.value, contra: selContraAccount?.value,
    lines: lines.map(({ uid, invoice_opt, ...line }) => line) }, initialDoc);
  return (<>
    {documentActions && <DocumentToolbar {...documentActions}
      title={`${initialDoc ? '' : 'Creează '}${isPlata ? 'plată' : 'încasare'}${initialDoc ? ` #${initialDoc.id}` : ''}`}
      dirty={draft.dirty} busy={busy} error={err || documentActions.error} isFinal={locked}
      onSave={() => formElement.current?.requestSubmit()} finalizeDisabled={draft.dirty} />}
    <Form ref={formElement} onPointerDownCapture={draft.interact} onKeyDownCapture={draft.interact} onChangeCapture={draft.interact}
      onSubmit={event => { if (busy || locked) { event.preventDefault(); return; } return handleSubmit(event); }}
      style={disabledAll ? { pointerEvents: "none", opacity: 0.6 } : undefined}
    >
      <fieldset disabled={disabledAll}>
      {err ? <Alert variant="danger">{err}</Alert> : null}

      <div className="mb-3 p-2 bg-light rounded border d-flex gap-4 align-items-center">
        <span className="fw-semibold text-secondary" style={{ fontSize: 14 }}>
          {isPlata ? "Variantă plată:" : "Variantă încasare:"}
        </span>
        <Form.Check
          type="radio"
          label={isPlata ? "1. Plată furnizori" : "1. Încasare clienți"}
          name="paymentMode"
          id="pm-furnizori"
          value="furnizori"
          checked={paymentMode === "furnizori"}
          onChange={(e) => {
            setPaymentMode(e.target.value);
            setSelContact(null);
            setSelContraAccount(null);
            setLines([emptyLine()]);
          }}
          disabled={disabledAll}
          inline
        />
        <Form.Check
          type="radio"
          label="2. Listă contacte"
          name="paymentMode"
          id="pm-contacte"
          value="contacte"
          checked={paymentMode === "contacte"}
          onChange={(e) => {
            setPaymentMode(e.target.value);
            setSelContact(null);
            setSelContraAccount(null);
            setLines([emptyLine()]);
          }}
          disabled={disabledAll}
          inline
        />
        <Form.Check
          type="radio"
          label="3. Fără contacte"
          name="paymentMode"
          id="pm-none"
          value="none"
          checked={paymentMode === "none"}
          onChange={(e) => {
            setPaymentMode(e.target.value);
            setSelContact(null);
            setSelContraAccount(null);
            setLines([emptyLine()]);
          }}
          disabled={disabledAll}
          inline
        />
      </div>

      {(() => {
        const showContraAccount = (paymentMode === "contacte" || paymentMode === "none");
        const isForeignCurrency = selAccount ? (selAccount.bxvaluta_id !== 1) : false;
        let contactMd = 3;
        let cashAccountMd = 3;
        let contraAccountMd = 0;

        if (paymentMode === "contacte") {
          contactMd = 2;
          contraAccountMd = 2;
          cashAccountMd = 2;
        } else if (paymentMode === "none") {
          if (isForeignCurrency) {
            contraAccountMd = 2;
            cashAccountMd = 2;
          } else {
            contraAccountMd = 3;
            cashAccountMd = 3;
          }
        } else {
          // furnizori / clienti
          if (isForeignCurrency) {
            contactMd = 2;
            cashAccountMd = 2;
          } else {
            contactMd = 3;
            cashAccountMd = 3;
          }
        }

        return (
          <Row className="g-2">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tip</Form.Label>
                <Form.Select
                  value={recType}
                  onChange={(e) => {
                    const newType = Number(e.target.value);
                    setRecType(newType);
                    setSelContact(null);
                    setSelContraAccount(null);
                    setLines([emptyLine()]);
                  }}
                >
                  <option value={20}>Încasare</option>
                  <option value={21}>Plată</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Nr</Form.Label>
                <Form.Control
                  value={docNo}
                  onChange={(e) => setDocNo(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Data</Form.Label>
                <DatePicker
                selected={docDate ? new Date(docDate) : null}
                onChange={(date) => {
                  if (!date) {
                    setDocDate("");
                    return;
                  }
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  setDocDate(`${year}-${month}-${day}T00:00:00`);
                }}
                dateFormat="dd.MM.yyyy"
                className="form-control"
                disabled={locked}
                placeholderText="Selectați data"
              />
              </Form.Group>
            </Col>

            {showContraAccount && (
              <Col md={contraAccountMd}>
                <Form.Group>
                  <Form.Label>Contra cont</Form.Label>
                  <AsyncSelect
                    key={`contra-${paymentMode}`}
                    cacheOptions
                    defaultOptions
                    loadOptions={loadContraAccounts}
                    value={selContraAccount}
                    onChange={(opt) => {
                      setSelContraAccount(opt);
                      if (paymentMode === "contacte") {
                        setSelContact(null);
                      }
                    }}
                    isClearable
                    isDisabled={disabledAll}
                    placeholder={
                      paymentMode === "contacte"
                        ? "Caută cont (switch=2)..."
                        : paymentMode === "none"
                        ? "Caută cont (switch=0)..."
                        : "Caută cont..."
                    }
                  />
                </Form.Group>
              </Col>
            )}

            {(paymentMode !== "none") && (
              <Col md={contactMd}>
                <Form.Group>
                  <Form.Label>Contact</Form.Label>
                  <AsyncSelect
                    key={paymentMode}
                    cacheOptions
                    defaultOptions
                    loadOptions={
                      paymentMode === "furnizori"
                        ? isPlata
                          ? loadSuppliersWithRest
                          : loadClientsWithRest
                        : loadContacts
                    }
                    value={selContact}
                    onChange={(opt) => {
                      setSelContact(opt);
                      setLines((prev) =>
                        prev.map((l) => ({
                          ...l,
                          ref_rec_type: null,
                          ref_doc_id: null,
                          invoice_opt: null,
                        })),
                      );
                    }}
                    isClearable
                    isDisabled={disabledAll || (paymentMode === "contacte" && !selContraAccount)}
                    placeholder={
                      paymentMode === "furnizori"
                        ? isPlata
                          ? "Caută furnizor..."
                          : "Caută client..."
                        : (paymentMode === "contacte" && !selContraAccount)
                        ? "Alege mai întâi contra contul..."
                        : "Caută contact..."
                    }
                  />
                </Form.Group>
              </Col>
            )}

            <Col md={cashAccountMd}>
              <Form.Group>
                <Form.Label>Cont casă/bancă</Form.Label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAccounts}
                  value={selAccount}
                  onChange={setSelAccount}
                  isClearable
                  isDisabled={disabledAll}
                  placeholder="Caută cont..."
                />
              </Form.Group>
            </Col>

            {isForeignCurrency && (
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Curs schimb</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    value={curs}
                    onChange={(e) => setCurs(e.target.value)}
                    disabled={disabledAll}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
        );
      })()}

      {paymentMode === "furnizori" ? (
        <Row className="g-2 mt-2">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Suma de distribuit</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={allocAmount}
                onChange={(e) => setAllocAmount(e.target.value)}
                disabled={disabledAll}
              />
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Button
              variant="outline-primary"
              type="button"
              onClick={openAllocModal}
              disabled={disabledAll || !selContact?.value}
            >
              Distribuie pe facturi…
            </Button>
          </Col>
          <Col className="d-flex align-items-end text-muted">
            {selContact?.value
              ? isPlata
                ? "Se deschide lista de facturi ale furnizorului."
                : "Se deschide lista de facturi ale clientului."
              : "Alege întâi contactul."}
          </Col>
        </Row>
      ) : null}

      <Row className="g-2 mt-1">
        <Col>
          <Form.Group>
            <Form.Label>Explicații</Form.Label>
            <Form.Control
              value={explic}
              onChange={(e) => setExplic(e.target.value)}
              placeholder="Observații"
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="mt-3 d-flex align-items-center justify-content-between">
        <div className="fw-semibold">Linii</div>
        <Button variant="outline-primary" type="button" onClick={addLine}>
          + Linie
        </Button>
      </div>

      <Table bordered size="sm" className="align-middle mt-2">
        <thead>
          <tr>
            <th style={{ width: 140 }} className="text-end">
              Suma
            </th>
            <th>Explicații</th>

            {paymentMode === "furnizori" ? (
              <th style={{ width: 360 }}>{isPlata ? "Factură furnizor" : "Factură client"}</th>
            ) : (
              <>
                <th style={{ width: 120 }} className="text-end">
                  Ref type
                </th>
                <th style={{ width: 140 }} className="text-end">
                  Ref doc id
                </th>
              </>
            )}

            <th style={{ width: 60 }}></th>
          </tr>
        </thead>

        <tbody>
          {lines.map((ln) => (
            <tr key={ln.uid}>
              <td className="text-end">
                <Form.Control
                  type="number"
                  step="0.01"
                  value={ln.amount}
                  onChange={(e) => setLine(ln.uid, { amount: e.target.value })}
                  className="text-end"
                />
              </td>

              <td>
                <Form.Control
                  value={ln.bxexplic}
                  onChange={(e) =>
                    setLine(ln.uid, { bxexplic: e.target.value })
                  }
                />
              </td>

              {paymentMode === "furnizori" ? (
                <td>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadInvoices}
                    value={ln.invoice_opt}
                    onChange={(opt) => {
                      setLine(ln.uid, {
                        invoice_opt: opt,
                        ref_rec_type: opt ? (isPlata ? 0 : 5) : null,
                        ref_doc_id: opt ? opt.value : null,
                      });
                    }}
                    isClearable
                    isDisabled={disabledAll || !selContact?.value}
                    placeholder={
                      selContact?.value
                        ? isPlata
                          ? "Alege factura din intrări..."
                          : "Alege factura din ieșiri..."
                        : "Alege întâi contactul"
                    }
                  />
                </td>
              ) : (
                <>
                  <td className="text-end">
                    <Form.Control
                      value={ln.ref_rec_type ?? ""}
                      onChange={(e) =>
                        setLine(ln.uid, {
                          ref_rec_type: e.target.value || null,
                        })
                      }
                      className="text-end"
                      placeholder="1/5/..."
                    />
                  </td>
                  <td className="text-end">
                    <Form.Control
                      value={ln.ref_doc_id ?? ""}
                      onChange={(e) =>
                        setLine(ln.uid, { ref_doc_id: e.target.value || null })
                      }
                      className="text-end"
                      placeholder="id"
                    />
                  </td>
                </>
              )}

              <td className="text-center">
                <Button
                  variant="outline-danger"
                  size="sm"
                  type="button"
                  onClick={() => removeLine(ln.uid)}
                >
                  ✕
                </Button>
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            <td className="text-end fw-semibold">
              {total.toFixed(2)} {selAccount?.bxvaluta_id === 2 ? "EURO" : selAccount?.bxvaluta_id === 3 ? "USD" : "RON"}
              {isForeignCurrency && (
                <div className="text-muted fw-normal" style={{ fontSize: 11 }}>
                  = {(total * Number(curs || 1)).toFixed(2)} RON
                </div>
              )}
            </td>
            <td colSpan={paymentMode === "furnizori" ? 2 : 4} className="text-muted">
              Totalul reprezintă suma liniilor. Valorile negative reprezintă storno.
            </td>
          </tr>
        </tfoot>
      </Table>

      {!documentActions && <div className="d-flex gap-2 mt-3">
        <Button type="submit" variant="primary">
          Salvează
        </Button>
      </div>}

      <Modal show={showAlloc} onHide={closeAllocModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Distribuie {Number(allocAmount || 0).toFixed(2)} pe facturi
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {invErr ? <Alert variant="danger">{invErr}</Alert> : null}

          <div className="d-flex gap-2 align-items-center mb-2">
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => autoDistribute(Number(allocAmount) || 0)}
              disabled={invLoading || !invoices.length}
            >
              Auto (FIFO)
            </Button>

            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => {
                const cleared = {};
                for (const inv of invoices)
                  cleared[inv.id] = { use: true, alloc: 0 };
                setAllocRows(cleared);
              }}
              disabled={invLoading || !invoices.length}
            >
              Clear
            </Button>

            <div className="ms-auto">
              <div className="text-muted" style={{ fontSize: 13 }}>
                Alocat: <b>{Number(allocSum).toFixed(2)}</b>
              </div>
            </div>
          </div>

          {invLoading ? (
            <div className="text-muted">
              <Spinner animation="border" size="sm" /> Se încarcă facturile…
            </div>
          ) : !invoices.length ? (
            <div className="text-muted">
              Nu am găsit facturi pentru acest furnizor.
            </div>
          ) : (
            <Table bordered size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: 44 }}></th>
                  <th>Factura</th>
                  <th style={{ width: 140 }} className="text-end">
                    Rest/Total
                  </th>
                  <th style={{ width: 160 }} className="text-end">
                    Plătesc
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const row = allocRows[inv.id] || { use: true, alloc: 0 };
                  return (
                    <tr key={inv.id}>
                      <td className="text-center">
                        <Form.Check
                          checked={!!row.use}
                          onChange={(e) =>
                            setAlloc(inv.id, { use: e.target.checked })
                          }
                        />
                      </td>
                      <td>
                        <div className="fw-semibold">{inv.doc_no}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {formatDateRo(inv.doc_date) || ""}
                        </div>
                      </td>
                      <td className="text-end">
                        {Number(inv.remaining).toFixed(2)} {inv.valuta || ""}
                      </td>
                      <td className="text-end">
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={row.alloc}
                          onChange={(e) =>
                            setAlloc(inv.id, { alloc: e.target.value })
                          }
                          className="text-end"
                          disabled={!row.use}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          <Alert variant="info" className="mt-2">
            Poți bifa/debifa facturi, sări peste unele și modifica manual suma
            pe fiecare.
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={closeAllocModal}>
            Renunță
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={applyAllocationToLines}
            disabled={invLoading}
          >
            Aplică în linii
          </Button>
        </Modal.Footer>
      </Modal>
      </fieldset>
    </Form></>
  );
}
